/* eslint-disable camelcase */
import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import Switch from '@material-ui/core/Switch';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';

import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { Helper } from '../../../common/helpers/Helper';
import { getListConfig } from '../../../common/helpers/ListFlatStorageHelper';
import { UiHelper } from '../../../common/helpers/UiHelper';
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta';
import { AppConfig } from '../../../AppConfig';
import { AppStyles } from '../../../styles/AppTheme';
import { SearchBar } from '../../../common/components/SearchBar';
import { PageDialog } from '../../../common/components/PageDialog';
import WhatsappSessionPreview from '../../../common/components/WhatsappSessionPreview';
import WhatsappTemplatePreview from '../../../common/components/WhatsappTemplatePreview';
import { AppUiHelper } from '../../../helpers/AppUiHelper';
import { buildPreviewComponentsFromTemplate } from './WhatsappPreviewAdapter';
import { buildWaNumberLabel, extractCrmNumbersItems, getWaOrgAccountNumbers, withVendorProjectIdQueryParam } from './WhatsappUtils';

const TYPE_TEMPLATE_INSTANCE = 'TEMPLATE_INSTANCE'
const TYPE_SESSION = 'SESSION'
const TYPE_CHATBOT = 'CHATBOT'

function formatTypeColumnLabel(iType) {
  const t = String(iType || '').toUpperCase()
  if (t === TYPE_TEMPLATE_INSTANCE) return 'TEMPLATE'
  return iType || '—'
}

function filterCreateITypeOptions(options) {
  return (options || []).filter(o => String((o && (o.key || o.value)) || '').toUpperCase() !== TYPE_CHATBOT)
}

function isWaContentLive(state) {
  return String(state || '').toUpperCase() === 'LIVE'
}

function withWaContentState(list, id, state) {
  return (list || []).map(item => (item && item.id === id ? { ...item, state } : item))
}

function getComponentsArray(current) {
  const components = current && current.components
  if (Array.isArray(components)) return components
  if (components && typeof components === 'object') return Helper.dictToArray(components)
  return []
}

function hasSessionInteractiveButton(current) {
  const buttonTypes = [
    String(current && current.button1Type || '').toUpperCase(),
    String(current && current.button2Type || '').toUpperCase(),
    String(current && current.button3Type || '').toUpperCase(),
  ]
  if (buttonTypes.some(t => t && t !== 'NONE')) return true
  const components = getComponentsArray(current)
  return components.some(c => {
    const cType = String(c && c.type || '').toUpperCase()
    return cType === 'BUTTONS' || c.buttonIndex != null
  })
}

function withIncomingBodyVariablePicker(waSessionMessageFieldDef, crmContactsFieldDef) {
  if (!waSessionMessageFieldDef) return null
  const fieldDef = JSON.parse(JSON.stringify(waSessionMessageFieldDef))
  const fields = fieldDef.steps && fieldDef.steps[0] && Array.isArray(fieldDef.steps[0].fields)
    ? fieldDef.steps[0].fields
    : []
  fields.forEach(field => {
    if (field && field.key === 'bodyRichText') {
      field.variablePicker = {
        title: 'Custom Fields',
        options: AppUiHelper.getContactTemplateVariables(crmContactsFieldDef),
      }
    }
  })
  return fieldDef
}

function normalizeWaContentsResponse(data, crmContactsFieldDef) {
  if (!data) return { items: [], fieldDef: null, waSessionMessageFieldDef: null }
  const items = (Array.isArray(data.items) ? data.items : []).filter(
    item => String((item && item.iType) || '').toUpperCase() !== TYPE_CHATBOT
  )
  return {
    items,
    fieldDef: data.fieldDef || null,
    waSessionMessageFieldDef: withIncomingBodyVariablePicker(data.waSessionMessageFieldDef, crmContactsFieldDef),
  }
}

function withSessionTypeCondition(field) {
  const iTypeCondition = { key: 'iType', value: TYPE_SESSION }
  const fieldCopy = JSON.parse(JSON.stringify(field || {}))
  if (!fieldCopy.condition) {
    fieldCopy.condition = iTypeCondition
    return fieldCopy
  }
  if (fieldCopy.condition.and && Array.isArray(fieldCopy.condition.and)) {
    const hasITypeCondition = fieldCopy.condition.and.some(c => c && c.key === 'iType')
    if (!hasITypeCondition) fieldCopy.condition.and.push(iTypeCondition)
    return fieldCopy
  }
  if (fieldCopy.condition.key === 'iType') return fieldCopy
  fieldCopy.condition = { and: [fieldCopy.condition, iTypeCondition] }
  return fieldCopy
}

const SESSION_PREVIEW_CONFIG = {
  component: 'session_preview_adapter',
  title: 'Preview',
  condition: { key: 'iType', value: TYPE_SESSION },
}

/** Legacy indexed keys replaced by `type: 'list'` (button*, row*). */
const LEGACY_FLAT_LIST_FIELD_KEY = /^(button\d+\w+|row\d+(Id|Title|Description))$/

const SESSION_INTERACTIVITY_AND_LIST_FIELDS = [
  {
    key: 'interactivityType',
    type: 'radio',
    label: 'Interactivity Type',
    options: [
      { key: 'NONE', label: 'NONE' },
      { key: 'BUTTON', label: 'BUTTON' },
      { key: 'LIST', label: 'LIST' },
    ],
    v_required: { value: true, err: 'Choose interactivity type' },
    placement: 'left',
    defaultValue: 'NONE',
  },
  {
    key: 'interactivityLabel',
    type: 'edit_text',
    condition: { key: 'interactivityType', value: 'LIST', values: ['LIST'] },
    label: 'Label',
    v_required: { value: true, err: 'Label is required' },
    placement: 'left',
  },
  {
    key: 'sectionTitle',
    type: 'edit_text',
    condition: { key: 'interactivityType', value: 'LIST', values: ['LIST'] },
    label: 'List Section Title',
    v_required: { value: true, err: 'List section title is required' },
    placement: 'left',
  },
  {
    key: 'button',
    type: 'list',
    condition: { key: 'interactivityType', value: 'BUTTON' },
    v_required: { value: false, err: '' },
    listConfig: {
      title: 'Buttons',
      addButtonText: 'Add Button',
      maxItems: 3,
      minItems: 1,
      flatClearValues: { Type: 'NONE' },
      flatEmptyValues: { Type: 'NONE' },
      addRowConstraint: {
        rowFieldKey: 'Type',
        maxItemsByValue: { URL: 1, FLOW: 1 },
        lockToFirstRowValue: true,
      },
      rowFields: [
        {
          key: 'Type',
          type: 'radio',
          label: 'Type',
          options: [
            { key: 'QUICK_REPLY', label: 'QUICK REPLY' },
            { key: 'URL', label: 'URL' },
            { key: 'FLOW', label: 'FLOW' },
          ],
          v_required: { value: true, err: 'Type is required' },
        },
        {
          key: 'Text',
          type: 'edit_text',
          label: 'Label',
          v_required: { value: true, err: 'Label is required' },
        },
        {
          key: 'Url',
          type: 'edit_text',
          condition: { key: 'Type', value: 'URL' },
          label: 'URL',
          v_required: { value: true, err: 'URL is required' },
        },
        {
          key: 'FlowId',
          type: 'radio',
          condition: { key: 'Type', value: 'FLOW' },
          label: 'Flow',
          options: [],
          v_required: { value: true, err: 'Flow is required' },
        },
      ],
    },
  },
  {
    key: 'row',
    type: 'list',
    condition: { key: 'interactivityType', value: 'LIST', values: ['LIST'] },
    v_required: { value: false, err: '' },
    listConfig: {
      title: 'Rows',
      addButtonText: 'Add Row',
      maxItems: 10,
      minItems: 1,
      flatStopAtEmptyRowKey: 'Id',
      rowFields: [
        {
          key: 'Id',
          type: 'edit_text',
          label: 'ID',
          v_required: { value: true, err: 'ID is required' },
        },
        {
          key: 'Title',
          type: 'edit_text',
          label: 'Title',
          v_required: { value: true, err: 'Title is required' },
        },
        {
          key: 'Description',
          type: 'edit_text',
          label: 'Description',
          v_required: { value: false, err: 'Description is required' },
        },
      ],
    },
  },
]

function deriveSessionInteractivityType(record) {
  const explicit = String(record && record.interactivityType || '').toUpperCase()
  if (explicit === 'NONE' || explicit === 'BUTTON' || explicit === 'LIST') {
    return explicit
  }
  if (record) {
    const b1 = String(record.button1Type || '').toUpperCase()
    if (b1 === 'LIST') return 'LIST'
    const b2 = String(record.button2Type || '').toUpperCase()
    const b3 = String(record.button3Type || '').toUpperCase()
    if ((b1 && b1 !== 'NONE') || (b2 && b2 !== 'NONE') || (b3 && b3 !== 'NONE')) {
      return 'BUTTON'
    }
  }
  return 'NONE'
}

function withSessionFormValuesFromRecord(record) {
  const values = { ...(record || {}) }
  const mode = deriveSessionInteractivityType(record)
  values.interactivityType = mode
  if (mode === 'LIST') {
    const label = String(values.interactivityLabel || values.button1Text || '').trim()
    if (label) {
      values.interactivityLabel = label
    }
  }
  return values
}

function normalizeSessionSubmitFormData(formData) {
  const out = { ...formData }
  const mode = String(out.interactivityType || 'NONE').toUpperCase()
  const interactivityLabel = out.interactivityLabel

  delete out.interactivityType
  delete out.interactivityLabel
  delete out.button
  delete out.row

  if (mode === 'LIST') {
    out.button1Type = 'LIST'
    out.button2Type = 'NONE'
    out.button3Type = 'NONE'
    out.button1Text = String(interactivityLabel || '').trim()
  }

  // Guard against conflicts where a key is both set and listed in REMOVE_ATTRS.
  if (Array.isArray(out.REMOVE_ATTRS) && out.REMOVE_ATTRS.length) {
    const setKeys = new Set(Object.keys(out))
    out.REMOVE_ATTRS = out.REMOVE_ATTRS.filter(key => !setKeys.has(key))
    if (!out.REMOVE_ATTRS.length) {
      delete out.REMOVE_ATTRS
    }
  }

  return out
}

function finishSessionSubmit(formData, caller, submitFn) {
  return submitFn(normalizeSessionSubmitFormData(formData))
}

function buildAllFlowOptions(waFromNumbers, waFlowsByNumber) {
  const numbers = [...new Set((waFromNumbers || []).map(n => String(n || '').trim()).filter(Boolean))]
  const seenKeys = new Set()
  const allOptions = []
  for (const wabaNumber of numbers) {
    const flows = (waFlowsByNumber || {})[wabaNumber] || []
    for (const flow of flows) {
      if (!flow || !flow.key || seenKeys.has(flow.key)) continue
      seenKeys.add(flow.key)
      allOptions.push({ key: flow.key, label: flow.key })
    }
  }
  allOptions.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
  return allOptions.length > 0
    ? allOptions
    : [{ key: '', label: 'No flows available' }]
}

function patchButtonInteractivityFlowFields(formDefinition, waFromNumbers, waFlowsByNumber) {
  const fields = formDefinition && formDefinition.steps && formDefinition.steps[0] && formDefinition.steps[0].fields
  if (!Array.isArray(fields)) return
  const allFlowOptions = buildAllFlowOptions(waFromNumbers, waFlowsByNumber)
  for (const field of fields) {
    const cfg = getListConfig(field)
    if (field && field.type === 'list' && field.key === 'button' && Array.isArray(cfg.rowFields)) {
      for (const rf of cfg.rowFields) {
        if (rf && rf.key === 'FlowId' && rf.type === 'radio') {
          rf.options = allFlowOptions
        }
      }
    }
  }
}

function getWaFromNumbers(waAccountsResult, orgId) {
  const waOrgAccounts = waAccountsResult && waAccountsResult[orgId]
  return getWaOrgAccountNumbers(waOrgAccounts)
}

function flowItemToSelectOption(item) {
  const name = String((item && item.name) || '').trim()
  if (!name) return null
  return { key: name, label: name }
}

async function fetchWhatsappFlowsByNumber(ms, orgId, fromNumbers, crmNumbers) {
  const numbers = [...new Set((fromNumbers || []).map(n => String(n || '').trim()).filter(Boolean))]
  const flowsByNumber = {}
  if (!numbers.length) return flowsByNumber
  const batches = await Promise.all(
    numbers.map(async (wabaNumber) => {
      try {
        const data = await ApiHelper.callAwait({
          ms,
          method: 'GET',
          paths: ['hooks'],
          queryParams: withVendorProjectIdQueryParam(
            { orgId, fromNumber: wabaNumber, bridgePath: 'whatsappflows' },
            wabaNumber,
            crmNumbers
          ),
        })
        return { wabaNumber, items: (data && data.items) || [] }
      } catch (e) {
        return { wabaNumber, items: [] }
      }
    })
  )
  for (const { wabaNumber, items } of batches) {
    flowsByNumber[wabaNumber] = items
      .map(flowItemToSelectOption)
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
  }
  return flowsByNumber
}

function getCreateTitleAndTypeFieldDef(fieldDef, waSessionMessageFieldDef) {
  const fields = fieldDef && fieldDef.steps && fieldDef.steps[0] && Array.isArray(fieldDef.steps[0].fields)
    ? fieldDef.steps[0].fields
    : []
  const sessionFields = waSessionMessageFieldDef && waSessionMessageFieldDef.steps && waSessionMessageFieldDef.steps[0] && Array.isArray(waSessionMessageFieldDef.steps[0].fields)
    ? waSessionMessageFieldDef.steps[0].fields
    : []
  const titleField = fields.find(f => f.key === 'title')
  const iTypeField = fields.find(f => f.key === 'iType')
  const additionalSessionFields = sessionFields
    .filter(f => !['title', 'iType'].includes(f.key))
    .filter(f => !LEGACY_FLAT_LIST_FIELD_KEY.test(f.key))
    .map(f => withSessionTypeCondition(f))
  const sessionExtraFields = SESSION_INTERACTIVITY_AND_LIST_FIELDS.map(withSessionTypeCondition)
  const fallback = {
    title: 'Add Message',
    ctaButtonText: 'Save',
    preview: SESSION_PREVIEW_CONFIG,
    steps: [{
      fields: [
        {
          key: 'title',
          type: 'edit_text',
          label: 'Nickname',
          v_required: { value: true, err: 'Provide nickname' },
          placement: 'left',
        },
        {
          key: 'iType',
          type: 'radio',
          label: 'Type',
          options: [
            { key: TYPE_TEMPLATE_INSTANCE, label: TYPE_TEMPLATE_INSTANCE },
            { key: TYPE_SESSION, label: TYPE_SESSION },
          ],
          v_required: { value: true, err: 'Choose type' },
          placement: 'right',
        },
        ...additionalSessionFields,
        ...sessionExtraFields,
      ],
    }],
  }
  if (!titleField || !iTypeField) return fallback

  const createITypeField = JSON.parse(JSON.stringify(iTypeField))
  createITypeField.placement = 'right'
  if (Array.isArray(createITypeField.options)) {
    createITypeField.options = filterCreateITypeOptions(createITypeField.options)
  }

  return {
    title: 'Add Message',
    ctaButtonText: 'Save',
    preview: SESSION_PREVIEW_CONFIG,
    steps: [{
      fields: [
        { ...JSON.parse(JSON.stringify(titleField)), placement: 'left' },
        createITypeField,
        ...additionalSessionFields,
        ...sessionExtraFields,
      ],
    }],
  }
}

function buildTableInfo(current, waNumberDetails) {
  const iType = String(current && current.iType || '').toUpperCase()
  if (iType === TYPE_TEMPLATE_INSTANCE) {
    return (
      <div>
        <span className={css(Styles.line2)}>
          <span className={css(Styles.infoFieldLabel)}>WABA</span>
          {' '}{buildWaNumberLabel(current.wabaNumber, waNumberDetails) || '—'}
        </span>
        <br />
        <span className={css(Styles.line2)}>
          <span className={css(Styles.infoFieldLabel)}>Template</span>
          {' '}{current.templateId || '—'}
        </span>
      </div>
    )
  }
  const interactive = hasSessionInteractiveButton(current)
  return (
    <div>
      <span className={css(Styles.line2)}>{String(current.headerType || 'NONE').toUpperCase()}</span>
      {interactive && (
        <>
          <br />
          <span className={css(Styles.tagInteractive)}>Interactive</span>
        </>
      )}
    </div>
  )
}

function filterRows(rows, searchText) {
  const q = String(searchText || '').trim().toLowerCase()
  if (!q) return rows || []
  return (rows || []).filter(r => {
    const title = String(r.title || '').toLowerCase()
    const iType = String(r.iType || '').toLowerCase()
    const templateId = String(r.templateId || '').toLowerCase()
    const wabaNumber = String(r.wabaNumber || '').toLowerCase()
    return title.includes(q) || iType.includes(q) || templateId.includes(q) || wabaNumber.includes(q)
  })
}

export class WhatsappContent extends BaseEntityPageSlixta {
  constructor(props) {
    super(props, { assetPicker: true })
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.pageTitle = 'WhatsApp messages'
    this.pageKey = 'whatsapp-messages'
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.waFromNumbers = []
    this.waNumberDetails = undefined
    this.waFlowsByNumber = {}
    this.noAdd = true
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: this.pageTitle },
    ]
    this.toFetch = [
      {
        key: 'waAccounts',
        ms: this.ms,
        method: 'GET',
        paths: ['miscs', 'get-wa-accounts'],
        queryParams: { orgId: this.orgId },
      },
      {
        key: 'waContents',
        ms: this.ms,
        method: 'GET',
        paths: ['wacontents'],
        queryParams: { orgId: this.orgId, pb: 'fieldDef,waSessionMessageFieldDef' },
      },
      {
        key: 'crmContacts',
        ms: this.ms,
        method: 'GET',
        paths: ['crmcontacts'],
        queryParams: { orgId: this.orgId, pb: 'fieldDef', includeCustomFields: 'true', limit: 1 },
      },
      {
        key: 'crmnumbers',
        ms: this.ms,
        method: 'GET',
        paths: ['crmnumbers'],
        queryParams: { orgId: this.orgId },
      },
    ]
    this.apiData = { orgId: this.orgId }
    this.tableHeadersMap = {
      Index: current => current.__index + 1,
      Nickname: current => (
        <div>
          <span className={css(Styles.line1)}>{current.title || '—'}</span>
        </div>
      ),
      Type: current => (
        <div>
          <span className={css(Styles.line2)}>{formatTypeColumnLabel(current.iType)}</span>
        </div>
      ),
      Info: current => buildTableInfo(current, this.waNumberDetails),
      Status: current => {
        const isLive = isWaContentLive(current.state)
        const isToggling = Boolean(this.state.togglingIds && this.state.togglingIds[current.id])
        return (
          <div className={css(Styles.statusCell)} onClick={e => e.stopPropagation()}>
            {isToggling ? (
              <CircularProgress size={20} style={{ margin: '0 8px' }} />
            ) : (
              <Switch
                checked={isLive}
                onChange={e => this.onToggleWaContentState(current, e.target.checked)}
                color='primary'
                size='small'
              />
            )}
          </div>
        )
      },
    }
    this.tableHeaders = Object.keys(this.tableHeadersMap)
    this.headerActions = [
      {
        label: 'Add Message',
        onClick: () => this.openModalForAddContent(),
      },
      {
        label: 'Refresh',
        tooltip: 'Refresh',
        icon: 'refresh',
        type: 'icon-label',
        onClick: () => this.fetchItems(),
      },
    ]
    this.state = {
      ...this.state,
      masterContents: [],
      searchText: '',
      waContentsFieldDef: null,
      waSessionMessageFieldDef: null,
      previewItem: null,
      previewComponents: [],
      previewType: '',
      previewTemplatesByNumber: {},
      isPreviewLoading: false,
      previewError: '',
      isPreviewDialogOpen: false,
      togglingIds: {},
    }
  }

  putWaContent(id, { title, state }) {
    return ApiHelper.callAwait({
      ms: this.ms,
      method: 'PUT',
      paths: ['wacontents', id],
      queryParams: { orgId: this.orgId },
      jsonBody: {
        orgId: this.orgId,
        title,
        state,
      },
    })
  }

  onToggleWaContentState = async (current, enabled) => {
    const id = current && current.id
    if (!id) return
    if (enabled === isWaContentLive(current.state)) return
    const state = enabled ? 'LIVE' : 'DISABLED'
    this.setState(prev => ({
      togglingIds: { ...(prev.togglingIds || {}), [id]: true },
    }))
    try {
      await this.putWaContent(id, {
        title: current.title || '',
        state,
      })
      this.setState(prev => ({
        masterContents: withWaContentState(prev.masterContents, id, state),
        items: withWaContentState(prev.items, id, state),
        togglingIds: { ...(prev.togglingIds || {}), [id]: false },
      }))
      const name = String(current.title || '').trim() || 'Message'
      this.showToast(`Message "${name}" is now ${state}`, 'success')
    } catch (err) {
      this.setState(prev => ({
        togglingIds: { ...(prev.togglingIds || {}), [id]: false },
      }))
      this.showToast(Helper.getErrorMsg(err), 'error')
    }
  }

  showToast = (message, severity = 'success') => {
    this.setState({
      showToast: true,
      toastMsg: message,
      isErrorToast: severity === 'error',
    })
  }

  async onFetchSuccess(results) {
    const result = this.getResult(results, 'waContents')
    const crmContactsResult = this.getResult(results, 'crmContacts')
    const crmContactsFieldDef = crmContactsResult && crmContactsResult.fieldDef ? crmContactsResult.fieldDef : null
    const normalized = normalizeWaContentsResponse(result, crmContactsFieldDef)
    this.waContentsFieldDef = normalized.fieldDef
    this.waSessionMessageFieldDef = normalized.waSessionMessageFieldDef
    const { fromNumbers, waNumberDetails } = getWaFromNumbers(this.getResult(results, 'waAccounts'), this.orgId)
    this.waFromNumbers = fromNumbers
    this.waNumberDetails = waNumberDetails
    this.crmNumbers = extractCrmNumbersItems(this.getResult(results, 'crmnumbers'))
    try {
      this.waFlowsByNumber = await fetchWhatsappFlowsByNumber(this.ms, this.orgId, this.waFromNumbers, this.crmNumbers)
    } catch (e) {
      this.waFlowsByNumber = {}
    }
    const items = filterRows(normalized.items, this.state.searchText)
    this.setState({
      masterContents: normalized.items,
      items,
      waContentsFieldDef: this.waContentsFieldDef,
      waSessionMessageFieldDef: this.waSessionMessageFieldDef,
    })
  }

  openContentModal(fd, modalState) {
    patchButtonInteractivityFlowFields(fd, this.waFromNumbers, this.waFlowsByNumber)
    this.formDefinition = fd
    this.setState({ modalOpen: true, ...modalState })
  }

  openModalForAddContent = () => {
    const fd = getCreateTitleAndTypeFieldDef(
      this.state.waContentsFieldDef || this.waContentsFieldDef,
      this.state.waSessionMessageFieldDef || this.waSessionMessageFieldDef
    )
    fd.formType = 'add'
    Helper.deleteFormDefinitionValues(fd)
    Helper.populateFormDefinitionValues(fd, {
      title: Helper.nextNonClashingIndexedName(
        'new_message_',
        (this.state.masterContents || []).map(c => c && c.title)
      ),
      iType: TYPE_TEMPLATE_INSTANCE,
      state: 'DISABLED',
      headerType: 'NONE',
      button1Type: 'NONE',
      button2Type: 'NONE',
      button3Type: 'NONE',
    })
    this.openContentModal(fd, {
      apiData: { waContentInitialCreate: true },
      apiMethod: 'POST',
      apiEndPoint: '',
    })
  }

  async createWaContent(formData) {
    return ApiHelper.callAwait({
      ms: this.ms,
      method: 'POST',
      paths: ['wacontents'],
      queryParams: { orgId: this.orgId },
      jsonBody: {
        ...formData,
        orgId: this.orgId,
      },
    })
  }
  openModalForCopySessionContent = (current) => {
    const fd = getCreateTitleAndTypeFieldDef(
      this.state.waContentsFieldDef || this.waContentsFieldDef,
      this.state.waSessionMessageFieldDef || this.waSessionMessageFieldDef
    )

    fd.title = 'Add Message'
    fd.formType = 'edit'
    Helper.deleteFormDefinitionValues(fd)
    const copyTitle = Helper.nextNonClashingIndexedName(
      `${current.title || 'message'}_copy_`,
      (this.state.masterContents || []).map(c => c && c.title)
    )

    Helper.populateFormDefinitionValues(fd, {
      ...current,
      title: copyTitle,
      iType: TYPE_SESSION,
      state: 'LIVE',
    })

    if (fd.steps && fd.steps[0] && Array.isArray(fd.steps[0].fields)) {
      fd.steps[0].fields.forEach(field => {
        if (field && field.key === 'iType') field.disabled = true
      })
    }

    this.formDefinition = fd

    this.setState({
      modalOpen: true,
      apiData: { waContentInitialCreate: true },
      apiMethod: 'POST',
      apiEndPoint: '',
    })
  }
  async customSubmit(options) {
    const { formType, formData, caller } = options

    if (formType === 'add' && this.formDefinition && this.formDefinition._subType === 'create-copy') {
      try {
        const response = await ApiHelper.callAwait({
          ms: AppConfig.CDP_MS.ENDPOINT,
          method: 'POST',
          paths: ['miscs', 'create-copy'],
          queryParams: { orgId: this.orgId},
          jsonBody: {
            ...this.apiData,
            ...formData,
          },
        })

        const iType = String(formData.iType || response.iType || '').toUpperCase()

        if (iType === 'TEMPLATE_INSTANCE') {
          this.props.history.push(`${this.baseRoute()}/whatsapp-messages/${response.id}`)
          return
        }

        if (iType === 'SESSION') {
          this.setState({ modalOpen: false }, () => {
            this.openModalForCopySessionContent(response)
          })
          return
        }
      } catch (err) {
        const errorMessage = Helper.getErrorMsg(err)
        caller.setState({
          fetchState: ApiHelper.State.ERROR,
          errMsg: errorMessage,
          success: false,
          message: errorMessage,
          inProcess: false,
        })
      }
      return
    }
    if (formData.waContentInitialCreate) {
      delete formData.waContentInitialCreate
      const iType = String(formData.iType || '').toUpperCase()
      if (iType !== TYPE_TEMPLATE_INSTANCE && iType !== TYPE_SESSION) {
        caller.setState({
          inProcess: false,
          success: false,
          message: 'Choose a valid content type.',
        })
        return
      }
      try {
        let sessionPayload
        if (iType === TYPE_TEMPLATE_INSTANCE) {
          sessionPayload = {
            title: formData.title,
            iType: TYPE_TEMPLATE_INSTANCE,
          }
        } else {
          sessionPayload = finishSessionSubmit(formData, caller, (normalized) => ({
            ...normalized,
            state: 'LIVE',
          }))
        }
        const response = await this.createWaContent(sessionPayload)
        caller.setState({ inProcess: false, success: true, message: '' })
        if (iType === TYPE_TEMPLATE_INSTANCE) {
          const id = response && (response.id || (response.item && response.item.id))
          if (!id) {
            throw new Error('Content was created but id was not returned.')
          }
          this.setState({ modalOpen: false }, () => {
            this.props.history.push(`${this.baseRoute()}/whatsapp-messages/${id}`)
          })
        } else {
          this.setState({ modalOpen: false }, () => this.fetchItems())
        }
      } catch (e) {
        caller.setState({
          inProcess: false,
          success: false,
          message: Helper.getErrorMsg(e),
        })
      }
      return
    }
    const normalized = finishSessionSubmit(formData, caller, (payload) => payload)
    return caller.submit.call(caller, normalized, true)
  }

  onSearch = (searchText = '') => {
    const items = filterRows(this.state.masterContents, searchText)
    this.setState({ searchText, items })
  }

  clearSearch = () => {
    const items = filterRows(this.state.masterContents, '')
    this.setState({ searchText: '', items })
  }

  openModalForEditContent = (current) => {
    const fd = getCreateTitleAndTypeFieldDef(
      this.state.waContentsFieldDef || this.waContentsFieldDef,
      this.state.waSessionMessageFieldDef || this.waSessionMessageFieldDef
    )
    fd.title = 'Edit WhatsApp Content'
    fd.formType = 'edit'
    const valuesForForm = current ? withSessionFormValuesFromRecord({ ...current, iType: String(current.iType || '').toUpperCase() })  : current
    Helper.populateFormDefinitionValues(fd, valuesForForm)
    const iType = String(current && current.iType || '').toUpperCase()
    if (iType === TYPE_SESSION && fd.steps && fd.steps[0] && Array.isArray(fd.steps[0].fields)) {
      fd.steps[0].fields.forEach((field) => {
        if (field && field.key === 'iType') field.disabled = true
      })
    }
    this.openContentModal(fd, {
      apiData: this.apiData,
      apiMethod: 'PUT',
      apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['wacontents', current.id], queryParams: { orgId: this.orgId } }),
    })
  }

  onDrillDown = (current) => {
    const iType = String(current && current.iType || '').toUpperCase()
    if (iType === TYPE_SESSION) {
      this.openModalForEditContent(current)
      return
    }
    if (iType === TYPE_TEMPLATE_INSTANCE) {
      this.props.history.push(`${this.baseRoute()}/whatsapp-messages/${current.id}`)
    }
  }

  openPreviewDialog = (current) => {
    this.setState({
      previewItem: current,
      previewComponents: [],
      previewType: TYPE_SESSION,
      isPreviewLoading: false,
      previewError: '',
      isPreviewDialogOpen: true,
    })
  }

  fetchWaTemplatesByFromNumber = async (fromNumber) => {
    if (!fromNumber) return []
    const data = await ApiHelper.callAwait({
      ms: this.ms,
      method: 'GET',
      paths: ['hooks'],
      queryParams: withVendorProjectIdQueryParam({
        orgId: this.orgId,
        fromNumber,
        bridgePath: 'whatsapptemplates',
      }, fromNumber, this.crmNumbers),
    })
    const templates = Array.isArray(data)
      ? data
      : (Array.isArray(data && data.items) ? data.items : [])
    return templates
      .filter(t => String(t && t.status || '').toUpperCase() === 'APPROVED')
  }

  openTemplatePreviewDialog = async (current) => {
    const fromNumber = current && current.wabaNumber
    const templateId = current && current.templateId
    if (!fromNumber || !templateId) {
      this.setState({
        previewItem: current,
        previewType: TYPE_TEMPLATE_INSTANCE,
        previewComponents: [],
        isPreviewLoading: false,
        previewError: 'Missing WABA number or template id.',
        isPreviewDialogOpen: true,
      })
      return
    }

    this.setState({
      previewItem: current,
      previewType: TYPE_TEMPLATE_INSTANCE,
      previewComponents: [],
      isPreviewLoading: true,
      previewError: '',
      isPreviewDialogOpen: true,
    })

    try {
      let templates = this.state.previewTemplatesByNumber[fromNumber]
      if (!templates) {
        templates = await this.fetchWaTemplatesByFromNumber(fromNumber)
      }
      const match = (templates || []).find(t => t && t.name === templateId)
      const templateStructure = Array.isArray(match && match.components)
        ? match.components
        : []
      const previewComponents = buildPreviewComponentsFromTemplate(
        templateStructure,
        current && current.components,
        { hideTemplateExamplesWhenUnset: true }
      )
      this.setState(prev => ({
        previewComponents,
        isPreviewLoading: false,
        previewError: '',
        previewTemplatesByNumber: templates === prev.previewTemplatesByNumber[fromNumber]
          ? prev.previewTemplatesByNumber
          : {
            ...prev.previewTemplatesByNumber,
            [fromNumber]: templates,
          },
      }))
    } catch (e) {
      this.setState({
        previewComponents: [],
        isPreviewLoading: false,
        previewError: Helper.getErrorMsg(e),
      })
    }
  }

  renderTemplatePreviewDialog() {
    const { previewType, previewItem, previewComponents, isPreviewLoading, previewError } = this.state
    let PageComponent = WhatsappSessionPreview
    let PageComponentProps = { session: previewItem || {} }
    if (previewType === TYPE_TEMPLATE_INSTANCE) {
      if (isPreviewLoading) {
        PageComponent = () => (
          <div className={css(Styles.previewMessage, Styles.previewLoading)}>
            {UiHelper.itemLoadingView(28)}
          </div>
        )
        PageComponentProps = {}
      } else if (previewError) {
        PageComponent = () => <div className={css(Styles.previewMessage, Styles.previewError)}>{previewError}</div>
        PageComponentProps = {}
      } else if (!previewComponents.length) {
        PageComponent = () => <div className={css(Styles.previewMessage)}>Template structure not found for this message.</div>
        PageComponentProps = {}
      } else {
        PageComponent = WhatsappTemplatePreview
        PageComponentProps = { components: previewComponents }
      }
    }
    return (
      <PageDialog
        PageComponent={PageComponent}
        PageComponentProps={PageComponentProps}
        title={(this.state.previewItem && this.state.previewItem.title) || '—'}
        fullWidthDialog={false}
        isOpen={this.state.isPreviewDialogOpen}
        onClosed={() => {
          this.setState({
            isPreviewDialogOpen: false,
            previewItem: null,
            previewComponents: [],
            previewType: '',
            isPreviewLoading: false,
            previewError: '',
          })
        }}
      />
    )
  }

  renderBelowTable() {
    return this.state.isPreviewDialogOpen ? this.renderTemplatePreviewDialog() : null
  }

  onActions = (current) => {
    const actions = []
    const iType = String(current && current.iType || '').toUpperCase()
    if (iType === TYPE_SESSION || iType === TYPE_TEMPLATE_INSTANCE) {
      actions.push(
        <Tooltip title='Preview' placement='top-start' key={`${current.id}-preview`}>
          <span className={css(AppStyles.entityTableLinkIcon)}>
            <i
              className='material-icons-outlined'
              onClick={(e) => {
                e.stopPropagation()
                if (iType === TYPE_SESSION) {
                  this.openPreviewDialog(current)
                } else {
                  this.openTemplatePreviewDialog(current)
                }
              }}
            >
              desktop_windows
            </i>
          </span>
        </Tooltip>
      )
    }
    const isSession = iType === TYPE_SESSION
    actions.push(
      <Tooltip title={isSession ? 'Edit' : 'Drill down'} placement='top-start' key={`${current.id}-drilldown`}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i
            className='material-icons-outlined'
            onClick={() => this.onDrillDown(current)}
          >
            {isSession ? 'edit' : 'arrow_forward_ios'}
          </i>
        </span>
      </Tooltip>
    )
    return actions
  }

  renderAboveTable() {
    return (
      <div className={css(Styles.toolbar)}>
        <SearchBar
          hint='Search by nickname / type / template'
          onSearch={this.onSearch}
          onClearSearch={this.clearSearch}
          searchText={this.state.searchText}
          searchButtonLabel='Filter'
          clearSearchLabel='Clear'
          autoRefresh
          debounceDuration={1}
          autoFocus={false}
          hideFiltersContainer
        />
      </div>
    )
  }
  onMenuItems = (current) => {
    const actions = []

    const menuItems = [
      {
        current: current,
        key: current.id + 'create-copy',
        action: () => {
          const iType = String(current && current.iType || '').toUpperCase()
          if (iType === 'TEMPLATE_INSTANCE') {
            UiHelper.openModalForCreateCopy.call(this, current, 'WACONTENT')
          }
          else if (iType === 'SESSION') {
            this.openModalForCopySessionContent(current)
          }
        },
        title: Helper.getString('createcopy'),
        icon: 'copy',
      },
    ]
    menuItems.map(item => {
      actions.push(UiHelper.actionButton(item))
    })
    return actions
  }
  onTableValue(current, index) {
    if (!this.tableHeaders[index]) return ''
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }
}

const Styles = StyleSheet.create({
  toolbar: {
    width: '100%',
    marginBottom: 16,
  },
  line1: {
    fontSize: 16,
  },
  line2: {
    fontSize: 14,
    color: 'gray',
  },
  infoFieldLabel: {
    fontWeight: 600,
    marginRight: 4,
  },
  tagInteractive: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    lineHeight: '16px',
    padding: '2px 10px',
    backgroundColor: '#DBEAFE',
    color: '#1E3A8A',
    letterSpacing: 0.2,
    textTransform: 'uppercase',
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
  },
  previewMessage: {
    padding: 16,
    color: '#4B5563',
    fontSize: 14,
    maxWidth: 420,
  },
  previewLoading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 120,
  },
  previewError: {
    color: '#991B1B',
  },
})
